import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/useAuthStore';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export default function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchProfile } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`Google login failed: ${errorParam}`);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      // Validate state (optional security check)
      const savedState = sessionStorage.getItem('oauth_state');
      if (savedState && state !== savedState) {
        setError('Invalid state parameter');
        return;
      }
      sessionStorage.removeItem('oauth_state');

      try {
        // Exchange code for token via backend
        await api.post('/api/pm/auth/external-login', {
          provider: 'google',
          code: code,
        });

        // Fetch user profile
        await fetchProfile();

        toast.success('Welcome to Letops!');
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        console.error('Google login error:', err);
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      }
    };

    handleCallback();
  }, [searchParams, navigate, fetchProfile]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Login Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/login"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}
